import React, { Component } from 'react';
import { ApplicationState } from 'store/rootReducer';
import { connect, ResolveThunks } from 'react-redux';
import Dropdown from 'react-bootstrap/Dropdown';
import { FormControl } from 'react-bootstrap';
import { BlazeMeterWorkspace } from 'types/rest/blazeMeterApiResponse';
import { switchWorkspaceThunk } from 'store/blazeUserConfig/blazeUserConfigActions';
import WorkspaceToggle from 'components/ui/workspaceSelect/toggle/WorkspaceToggle';
import { WithTranslation, withTranslation } from 'react-i18next';
import BzmIcon from '../../icons/BzmIcon';

const mapStateToProps = (state: ApplicationState) => {
  const blazeUserConfig = state.blazeUserConfig.blazeUserConfig;
  if (blazeUserConfig) {
    return {
      activeWorkspaceId: blazeUserConfig.user.activeWorkspaceId,
      workspaces: blazeUserConfig.user.workspaces,
    };
  }
};

const mapDispatchToProps = {
  switchWorkspace: switchWorkspaceThunk,
};

type StateProps = {
  activeWorkspaceId: number;
  workspaces: BlazeMeterWorkspace[];
};

export type Props = StateProps & ResolveThunks<typeof mapDispatchToProps> & WithTranslation;

type State = {
  workspaceNameFilter: string;
  workspaceFilterShown: boolean;
};

export class WorkspaceSelect extends Component<Props, State> {
  state = {
    workspaceNameFilter: '',
    workspaceFilterShown: false,
  };

  pickerMenuStyles = {
    minWidth: '300px',
    width: '100%',
    zIndex: 3000,
  };

  changeWorkspace = (id: number) => {
    this.props.switchWorkspace(id);
  };

  handleWorkspaceFilterChange = (event: any) => {
    const searchValue = event.currentTarget.value as string;
    this.setState({
      workspaceNameFilter: searchValue,
    });
  };

  handleWorkspaceFilterReset = () => {
    this.setState({ workspaceNameFilter: '' });
  };

  handleWorkspaceFilterToggle = () => {
    this.setState(prevState => ({
      workspaceFilterShown: !prevState.workspaceFilterShown,
      workspaceNameFilter: '',
    }));
  };

  render() {
    const { t } = this.props;
    const activeWorkspace = this.props.workspaces.find(ws => ws.id === this.props.activeWorkspaceId);

    const wsItems = this.props.workspaces.map((ws: BlazeMeterWorkspace) => {
      if (this.state.workspaceNameFilter !== '') {
        if (ws.name.toLowerCase().indexOf(this.state.workspaceNameFilter.toLowerCase()) === -1) {
          return null;
        }
      }
      // don't show disabled workspaces
      if (!ws.enabled) {
        return null;
      }
      return (
        <Dropdown.Item id={`qa-workspace-item-${ws.id}`} onClick={() => this.changeWorkspace(ws.id)} key={ws.id}>
          {this.props.activeWorkspaceId === ws.id && (
            <span className="indicator indicator-active indicator-sm float-right mt-3">
              <span className="sr-only">{t('workspaceSelect.activeIndicator')}</span>
            </span>
          )}
          {ws.name}
        </Dropdown.Item>
      );
    });

    // header to be displayed when the search is open
    const searchOpenHeader = (
      <div className="dropdown-header">
        <span
          className="cursor-pointer float-right"
          aria-label={t('actions.close')}
          onClick={() => this.handleWorkspaceFilterToggle()}
          role="button"
          aria-hidden="true"
        >
          <BzmIcon variant={'icon-bzm-close-remove'}></BzmIcon>
          <span className="sr-only">{t('actions.close')}</span>
        </span>
        <span className="dropdown-title">{t('workspaceSelect.workspacesTitle')}</span>
        <div className="has-form-control-icon has-form-control-icon-left mt-1">
          <FormControl
            autoFocus
            autoComplete="off"
            placeholder={t('actions.search')}
            style={{ width: '100%', paddingRight: '30px' }}
            aria-label={t('actions.search')}
            value={this.state.workspaceNameFilter}
            onChange={this.handleWorkspaceFilterChange}
            id="qa-input-workspace-search"
          />
          <span className="form-control-icon form-control-icon-left">
            <BzmIcon variant={'icon-bzm-search'} className={'icon-theme-gray-50'} />
            <span className="sr-only">{t('actions.search')}</span>
          </span>
          {this.state.workspaceNameFilter !== '' && (
            <button
              onClick={() => this.handleWorkspaceFilterReset()}
              className="form-control-icon form-control-icon-right btn btn-link btn-no-hover"
              style={{ padding: 0, pointerEvents: 'all', textDecoration: 'none' }}
            >
              <span
                className="icon icon-bzm-close icon-theme-gray-50"
                style={{ fontSize: '.7rem' }}
                aria-hidden="true"
              />
              <span className="sr-only">{t('actions.clear')}</span>
            </button>
          )}
        </div>
      </div>
    );

    // header to be displayed when the search is closed
    const searchClosedHeader = (
      <div>
        <h6 className="dropdown-header" style={{ position: 'relative' }}>
          <span
            id="qa-btn-workspace-search-toggle"
            className="cursor-pointer float-right"
            aria-label={t('actions.search')}
            onClick={() => this.handleWorkspaceFilterToggle()}
            role="button"
          >
            <BzmIcon variant={'icon-bzm-search'} />
            <span className="sr-only">{t('actions.clear')}</span>
          </span>
          <span className="dropdown-title">{t('workspaceSelect.workspacesTitle')}</span>
        </h6>
      </div>
    );

    return (
      <Dropdown>
        <Dropdown.Toggle id="workspace-select" as={WorkspaceToggle}>
          {activeWorkspace ? activeWorkspace.name : ''}
        </Dropdown.Toggle>

        <Dropdown.Menu style={this.pickerMenuStyles}>
          {this.state.workspaceFilterShown ? searchOpenHeader : searchClosedHeader}
          <div className="dropdown-menu-limited">{wsItems}</div>
        </Dropdown.Menu>
      </Dropdown>
    );
  }
}

export default withTranslation('common')(connect(mapStateToProps, mapDispatchToProps)(WorkspaceSelect));
